import { Injectable } from '@angular/core';
import { OrigamiRequestRestParams, OrigamiResponse, ServiceBase } from './base/service.base';
import { Observable } from 'rxjs';
import { SimpleTopic, TopicField } from '../utils/topic-utils';
import { SSUEssentialDocument } from '@app/origami-api/services/essential-documents.service';
import { RequiredProvidedLookup } from '@app/origami-api/constants/required-documents';
import AmendmentProfileView = AmendmentProfileServiceModel.AmendmentProfileView;
import AmendmentProfileItem = AmendmentProfileServiceModel.AmendmentProfileItem;

export namespace AmendmentProfileServiceModel {
  import EssentialFor = SSUEssentialDocument.EssentialFor;

  export interface BaseAmendmentProfileOptions {
    siteId: number;
    amendmentProfileTopicId?: number;
  }

  export type GetAmendmentProfileOptions = BaseAmendmentProfileOptions;

  export interface CreateAmendmentProfileOptions {
    siteId: number;
    amendmentProfile: {
      SiteId: number;
      TopicName: string;
      RequiredDocuments: RequiredDocument[];
      TopicFields: TopicField[];
    };
  }

  export interface AmendmentProfileView {
    EntitiesCount: number;
    ViewFilterDisplayName: string;
    ViewFilterId: string;
  }

  export interface GetAmendmentProfilesOptions {
    siteId: number;
    start?: number;
    limit?: number;
    searchClause?: string;
    orderBy?: string;
    viewMode?: string;
    viewFilter?: number | string;
  }
  export interface GetEntityAmendmentsOptions {
    siteId: number;
    start?: number;
    limit?: number;
    orderBy?: string;
    essentialFor: SSUEssentialDocument.EssentialFor;
    essentialForId: number | string;
    searchQuery?: string;
    viewMode?: number;
    viewFilter?: number;
  }

  export interface GetAmendmentProfilesViewsOptions {
    siteId: number;
    viewMode?: number;
  }
  export interface GetEntityAmendmentsViewsViewsOptions {
    siteId: number;
    essentialFor: EssentialFor;
    essentialForId: string;
    viewMode: number;
  }

  export interface GetEntityAmendmentOptions {
    siteId: number;
    essentialFor: SSUEssentialDocument.EssentialFor;
    essentialForId: number | string;
    amendmentProfileId: number;
  }
  export interface SetNotApplicableAmendmentsOptions {
    siteId: number;
    essentialFor: SSUEssentialDocument.EssentialFor;
    essentialForIds: string[];
    amendmentProfileId: number;
    comments: string;
  }

  export interface SetNotApplicableAmendmentsStatusOptions {
    siteId: number;
    essentialFor: SSUEssentialDocument.EssentialFor;
    essentialForIds: string[];
    amendmentProfileIds: number[];
    comments?: string;
    notApplicable: boolean;
  }
  export interface CompleteAmendmentOptions {
    siteId: number;
    essentialFor: SSUEssentialDocument.EssentialFor;
    essentialForId: string;
    amendmentId: number;
    doNotMoveAnyDocumentsToEtmf: boolean;
    documentIdsMovingToEtmf: string[];
  }

  export interface GetAmendmentProfileFiltersOptions {
    siteId: number;
    essentialFor: SSUEssentialDocument.EssentialFor;
    essentialForId?: string;
  }

  export enum RequiredByEnum {
    Country = 1,
    IRB = 2,
    InvestigativeSite = 3,
  }

  export interface ModifyAmendmentProfileOptions {
    siteId: number;
    amendmentProfile: AmendmentProfile;
  }

  export interface AmendmentProfile {
    CreatedBy: number;
    CreatedDate: string;
    LockedBy: null;
    ParentId: null;
    SiteId: number;
    TopicFields: TopicField[];
    TopicId: number;
    TopicName: string;
    TopicTypeId: number;
    TopicWorkflowStageFields: any[];
    RequiredDocuments: RequiredDocument[];
  }

  export interface RequiredDocument {
    CreatedBy?: number;
    CreatedDate?: string;
    DocumentTypeId: number;
    EventTopicId?: null;
    EventTypes?: null;
    LastUpdatedBy?: number;
    LastUpdatedDate?: string;
    MissingDocumentTypeFields?: null;
    PlaceholderTopicId?: null;
    ProvidedBy: RequiredProvidedLookup;
    ProvidedById: null | string;
    RequiredBy: RequiredProvidedLookup;
    RequiredById: string;
    RequiredDocumentId?: number;
    Contacts?: number[];
    Languages?: string[];
    RequiredDocumentMetadata: TopicField[];
    SiteId: number;
  }

  export interface AmendmentProfileItem {
    SiteId: number;
    TopicFields: TopicField[];
    TopicName: string;
    CreatedBy: number;
    CreatedDate: string;
    LockedBy: null;
    ParentId: null;
    TopicId: number;
    TopicTypeId: number;
    TopicWorkflowStageFields: any[];
    RequiredDocuments: RequiredDocument[];
  }

  export interface AmendmentEntityItem extends SimpleTopic {
    SiteId: number;
    TopicFields: TopicField[];
    TopicName: string;
  }

  export class GetAmendmentProfilesResult {
    AmendmentProfiles: AmendmentProfileItem[];
    TotalRowCount: number;
    PageNumber: number;
  }

  export class GetAmendmentProfileFilters {
    AmendmentProfileId: number;
    AmendmentProfileName: string;
    EntityAmendmentAllDocumentsCount?: number;
    EntityAmendmentApprovalPercentage?: number;
    EntityAmendmentSSUDocumentsCount?: number;
    EntityAmendmentStatusId?: EntityAmendmentStatusId;
  }

  export enum EntityAmendmentStatusId {
    Pending = 1,
    Completed,
  }

  export class GetEntityAmendmentsResult {
    Records: AmendmentEntityItem[];
    TotalRowCount: number;
    PageNumber: number;
  }
  export class GetEntityAmendmentResult {
    AccessRight: number;
    AmendmentId: number;
    AmendmentStatusId: number;
    CurrentUserCanCompleteAmendment: boolean;
    Progress: number;
    SSUFolderId: number;
    TopicFields: TopicField[];
  }
}

@Injectable()
export class AmendmentProfileService extends ServiceBase {
  serviceUrl = 'AmendmentProfileService.svc';

  getAmendmentProfile(
    body: AmendmentProfileServiceModel.GetAmendmentProfileOptions,
    ...params: OrigamiRequestRestParams
  ): Observable<OrigamiResponse<AmendmentProfileItem>> {
    return this.origamiPost('/GetAmendmentProfile', body, ...params);
  }

  getAmendmentProfiles(
    body: AmendmentProfileServiceModel.GetAmendmentProfilesOptions,
    ...params: OrigamiRequestRestParams
  ): Observable<OrigamiResponse<AmendmentProfileServiceModel.GetAmendmentProfilesResult>> {
    return this.origamiPost('/GetAmendmentProfiles', body, ...params);
  }

  getAmendmentProfileFilters(
    body: AmendmentProfileServiceModel.GetAmendmentProfileFiltersOptions,
    ...params: OrigamiRequestRestParams
  ): Observable<OrigamiResponse<AmendmentProfileServiceModel.GetAmendmentProfileFilters[]>> {
    return this.origamiPost('/GetAmendmentProfileFilters', body, ...params);
  }

  createAmendmentProfile(body: AmendmentProfileServiceModel.CreateAmendmentProfileOptions, ...params): Observable<any> {
    return this.origamiPost('/CreateAmendmentProfile', body, null, null, true);
  }

  modifyAmendmentProfile(body: AmendmentProfileServiceModel.ModifyAmendmentProfileOptions): Observable<any> {
    return this.origamiPost('/ModifyAmendmentProfile', body, null, null, true);
  }

  getEntityAmendments(
    body: AmendmentProfileServiceModel.GetEntityAmendmentsOptions,
    ...params: OrigamiRequestRestParams
  ): Observable<OrigamiResponse<AmendmentProfileServiceModel.GetEntityAmendmentsResult>> {
    return this.origamiPost('/GetEntityAmendments', body, ...params);
  }

  getAmendmentProfilesViews(
    body: AmendmentProfileServiceModel.GetAmendmentProfilesViewsOptions,
  ): Observable<OrigamiResponse<AmendmentProfileView[]>> {
    return this.origamiPost('/GetAmendmentProfilesViews', body, null, null, true);
  }

  getEntityAmendmentsViews(
    body: AmendmentProfileServiceModel.GetEntityAmendmentsViewsViewsOptions,
  ): Observable<OrigamiResponse<AmendmentProfileView[]>> {
    return this.origamiPost('/GetEntityAmendmentsViews', body, null, null, true);
  }
}
